import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, ShieldCheck, ShieldWarning, Shield, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import EditRowSheet from "@/components/EditRowSheet";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

const CATEGORIES = ["AHU", "VRF", "Chiller", "FCU", "Pump", "Cooling Tower", "Ducting", "BMS", "Other"];

function WarrantyPill({ status, expiry }) {
  const map = {
    active: { c: "bg-emerald-50 text-emerald-700", i: ShieldCheck },
    expiring: { c: "bg-amber-50 text-amber-700", i: ShieldWarning },
    expired: { c: "bg-red-50 text-red-700", i: ShieldWarning },
    unknown: { c: "bg-slate-100 text-slate-600", i: Shield },
  };
  const s = map[status] || map.unknown;
  const Ico = s.i;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${s.c}`}>
      <Ico size={11} />{status}{expiry ? ` · ${expiry}` : ""}
    </span>
  );
}

export default function EquipmentRegistry({ projectId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = {
    project_id: projectId || "", name: "", tag_no: "", category: "AHU",
    manufacturer: "", model: "", serial_no: "", location: "",
    install_date: "", commissioning_date: "", warranty_months: 12, notes: "",
  };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/equipment", { params: projectId ? { project_id: projectId } : {} }),
      api.get("/projects"),
    ]);
    setRows(a.data); setProjects(b.data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  const submit = async () => {
    try {
      await api.post("/equipment", {
        ...form,
        project_id: projectId || form.project_id || null,
        warranty_months: parseInt(form.warranty_months) || 0,
      });
      toast.success("Equipment added");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this equipment record?")) return;
    try { await api.delete(`/equipment/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const formSheet = (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-testid="create-equipment-button" size={embedded ? "sm" : "default"} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
          <Plus size={14} className="mr-1" /> Add Equipment
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[560px] sm:max-w-xl overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">Register Equipment</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-6">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name / Description"><Input data-testid="eq-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Chiller-01" /></Field>
            <Field label="Tag No."><Input value={form.tag_no} onChange={(e) => setForm({ ...form, tag_no: e.target.value })} placeholder="CH-01" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {!projectId && (
              <Field label="Project">
                <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Manufacturer"><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} placeholder="Carrier / Daikin / Blue Star" /></Field>
            <Field label="Model"><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="30XA1002" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Serial No."><Input data-testid="eq-serial-input" value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} placeholder="CR2026001" /></Field>
            <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Terrace plant room" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Install date"><Input type="date" value={form.install_date} onChange={(e) => setForm({ ...form, install_date: e.target.value })} /></Field>
            <Field label="Commissioning date"><Input type="date" value={form.commissioning_date} onChange={(e) => setForm({ ...form, commissioning_date: e.target.value })} /></Field>
            <Field label="Warranty (months)"><Input type="number" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button onClick={submit} data-testid="save-equipment-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Register</Button>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div data-testid="equipment-page">
      {!embedded && (
        <PageHeader
          title="Equipment Registry"
          subtitle="All HVAC equipment installed across projects — model, serial, and warranty tracking"
          module="Service · SV"
          actions={formSheet}
        />
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">Equipment ({rows.length})</div>
          {formSheet}
        </div>
      )}

      <div className={embedded ? "" : "p-8"}>
        {rows.length === 0 ? (
          <EmptyState title="No equipment registered" description="Add chillers, AHUs, VRF units, pumps and more to enable warranty tracking, PPM schedules, and service ticket linkage." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="equipment-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Tag</TableHead>
                  <TableHead className="label-caps">Name</TableHead>
                  <TableHead className="label-caps">Category</TableHead>
                  <TableHead className="label-caps">Model / Serial</TableHead>
                  <TableHead className="label-caps">Location</TableHead>
                  <TableHead className="label-caps">Project</TableHead>
                  <TableHead className="label-caps">Installed</TableHead>
                  <TableHead className="label-caps">Warranty</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`equipment-row-${r.id}`}>
                    <TableCell className="font-mono-num font-semibold text-[#EA580C]">{r.tag_no || "—"}</TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-slate-600 text-xs uppercase tracking-wider">{r.category || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">
                      <div>{r.manufacturer} {r.model}</div>
                      <div className="font-mono-num text-slate-400">{r.serial_no}</div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-xs">{r.location || "—"}</TableCell>
                    <TableCell className="text-slate-600 text-xs">{projMap[r.project_id] || "—"}</TableCell>
                    <TableCell className="text-slate-600 font-mono-num text-xs">{fmtDate(r.install_date) || "—"}</TableCell>
                    <TableCell><WarrantyPill status={r.warranty_status} expiry={r.warranty_expiry} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setEditing(r)} data-testid={`edit-equipment-${r.id}`} className="text-slate-500 hover:text-[#EA580C] p-1" title="Edit"><PencilSimple size={13} /></button>
                        <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {editing && (
        <EditRowSheet
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          title={`Edit Equipment · ${editing.tag_no || editing.name}`}
          endpoint={`/equipment/${editing.id}`}
          initial={editing}
          fields={[
            { name: "name", label: "Name", required: true },
            { name: "tag_no", label: "Tag / Asset #" },
            {
              name: "category", label: "Category", type: "select",
              options: CATEGORIES.map((c) => ({ value: c, label: c })),
            },
            { name: "location", label: "Location" },
            { name: "manufacturer", label: "Manufacturer" },
            { name: "model", label: "Model" },
            { name: "serial_no", label: "Serial #" },
            { name: "warranty_months", label: "Warranty (months)", type: "number" },
            { name: "install_date", label: "Install date", type: "date" },
            { name: "commissioning_date", label: "Commissioning date", type: "date" },
            { name: "notes", label: "Notes", type: "textarea", colSpan: 2 },
          ]}
          transformPayload={(f) => ({
            ...f,
            warranty_months: Number(f.warranty_months || 0),
          })}
          onSaved={load}
        />
      )}
    </div>
  );
}
